import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import BankAccounts from "@/pages/BankAccounts";
import TaxSavings from "@/pages/TaxSavings";
import Debts from "@/pages/Debts";
import AIAdvisor from "@/pages/AIAdvisor";
import Statements from "@/pages/Statements";
import PlanDetail from "@/pages/PlanDetail";
import SharePublic from "@/pages/SharePublic";
import Reports from "@/pages/Reports";
import Integrations from "@/pages/Integrations";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import Clients from "@/pages/Clients";
import ClientPortal from "@/pages/ClientPortal";
import Messages from "@/pages/Messages";
import BackOffice from "@/pages/BackOffice";
import FirmDashboard from "@/pages/FirmDashboard";
import SuperAdmin from "@/pages/SuperAdmin";
import SuperAdminSettings from "@/pages/SuperAdminSettings";
import Onboard from "@/pages/Onboard";

function landingFor(role) {
  if (role === "client") return "/client";
  if (role === "admin") return "/super-admin";
  return "/app";
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={landingFor(user.role)} replace />;
  return children;
}

function ClientOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "client") return <Navigate to={landingFor(user.role)} replace />;
  return children;
}

function AccountantOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "client") return <Navigate to="/client" replace />;
  if (user.role === "admin") return <Navigate to="/super-admin" replace />;
  return children;
}

function SuperAdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to={landingFor(user.role)} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/share/:slug" element={<SharePublic />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/onboard" element={<PublicOnly><Onboard /></PublicOnly>} />
      <Route path="/client" element={<ClientOnly><ClientPortal /></ClientOnly>} />
      <Route path="/super-admin" element={<SuperAdminOnly><SuperAdmin /></SuperAdminOnly>} />
      <Route path="/super-admin/settings" element={<SuperAdminOnly><SuperAdminSettings /></SuperAdminOnly>} />
      <Route path="/app" element={<AccountantOnly><AppLayout /></AccountantOnly>}>
        <Route index element={<Dashboard />} />
        <Route path="accounts" element={<BankAccounts />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="statements" element={<Statements />} />
        <Route path="tax" element={<TaxSavings />} />
        <Route path="debts" element={<Debts />} />
        <Route path="advisor" element={<AIAdvisor />} />
        <Route path="reports" element={<Reports />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="billing" element={<Billing />} />
        <Route path="settings" element={<Settings />} />
        <Route path="clients" element={<Clients />} />
        <Route path="messages" element={<Messages />} />
        <Route path="back-office" element={<BackOffice />} />
        <Route path="firm" element={<FirmDashboard />} />
        <Route path="plan/:id" element={<PlanDetail />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
